import type { RadialGradientBGProps } from '@jouzen/ecom-components';
import { GridItem, TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';

import ImageWrapper from './ImageWrapper';
import type { LandingPageFeature } from './types';

export interface MobileFeatureProps extends LandingPageFeature {
  readonly index: number;
  readonly imageGradientBg?: RadialGradientBGProps | undefined;
}

const MobileFeature = ({
  description,
  image,
  imageGradientBg,
  index,
  title,
}: MobileFeatureProps): JSX.Element => {
  const t = useTranslations();
  return (
    <>
      <GridItem
        colEnd={{ sm: 'main', md: 20 }}
        colStart={{ sm: 'main', md: 6 }}
      >
        <Motion
          initial={{ filter: 'blur(5px)', opacity: 0 }}
          whileInView={{ filter: 'blur(0px)', opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <TypographyV3
            className={cx('mb-4 font-normal', { 'pt-16': index !== 0 })}
            color="inherit"
            data-cy="landing-page-mobile-feature-title"
            Element="h3"
            variant="h4"
          >
            {t(title)}
          </TypographyV3>
        </Motion>
        <Motion
          initial={{ filter: 'blur(5px)', opacity: 0, y: 20 }}
          whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <TypographyV3 Element="p" color="inherit" className="mb-6">
            {t(description)}
          </TypographyV3>
        </Motion>
      </GridItem>
      <GridItem
        colEnd={{ sm: 'full', md: 20 }}
        colStart={{ sm: 'full', md: 6 }}
      >
        <ImageWrapper imageGradientBg={imageGradientBg}>
          <Motion
            className="relative size-full"
            initial={{ filter: 'blur(5px)', opacity: 0, y: 50 }}
            whileInView={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <Image {...image} fill sizes="(max-width: 767px) 100vw, 60vw" />
          </Motion>
        </ImageWrapper>
      </GridItem>
    </>
  );
};

export default MobileFeature;
